import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import * as Yup from "yup";
import moment from "moment";

import { BottomSheet, Drawer, PaymentServicesArca, Box, Spinner } from "@ui-kit/components/index.es";

import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as servicePaymentsActions, selectors as servicePaymentsSelectors } from "reducers/servicePayments";
import { actions as paymentsAFIPActions, selectors as paymentsAFIPSelectors } from "reducers/paymentsAFIP";
import { actions as notificationActions } from "reducers/notification";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as settingsSelectors } from "reducers/settings";
import { actions as statusActions, selectors as statusSelectors } from "reducers/status";

import { FORM_ID, FORM_LIST_ID } from "constants/servicePayments";

import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import ContextMenu from "pages/_components/ContextMenu";
import AFIPServiceConditions from "pages/enabledContributors/_components/AFIPServiceConditions";

import * as configUtils from "util/config";
import * as i18n from "util/i18n";
import { saveLocation } from "util/transaction";
import {
    applyEmbeddedV3ShellLayoutState,
    clearEmbeddedV3ShellLayoutState,
    isUxDashboardImprovementsEnabled,
} from "util/v3ShellLayoutSync";
import { getPostLoginHomePath } from "util/v3UxConfig";
import { isServicePaymentsPath } from "util/servicePaymentsRoutes";
import { buildPreparePaymentsRequest, buildSummaryInfo } from "util/servicePayments";
import {
    flatPaymentToArcaTaxServiceRow,
    extractArcaFlatPaymentPatchFromServiceRow,
    getPaymentsArcaServiceCardLabel,
    mapArcaPaidVepFiltersToFetchParams,
    mapPaymentsAFIPToVepPaidCardData,
} from "util/paymentsServicesArcaMappers";

import { resizableRoute } from "pages/_components/Resizable";

function mergeFlatPaymentWithArcaServiceRow(flatPayment, serviceRow) {
    const patch = extractArcaFlatPaymentPatchFromServiceRow(serviceRow);
    return {
        ...flatPayment,
        amount: {
            ...flatPayment.amount,
            quantity:
                patch.quantity !== undefined && Number.isFinite(patch.quantity)
                    ? patch.quantity
                    : flatPayment.amount.quantity,
        },
        paymentReference: patch.paymentReference !== undefined ? patch.paymentReference : flatPayment.paymentReference,
    };
}

function isPesosAccount(account) {
    const c = account?.currency;
    if (c === null || c === undefined) {
        return false;
    }
    return c === 0 || c === "0" || String(c) === "0";
}

function isEligibleExitHref(pathname) {
    return Boolean(pathname && !isServicePaymentsPath(pathname));
}

function buildArcaPendingInitialValues(hasOwn, hasThirdparty, hasTaxpayer) {
    return {
        own: hasOwn,
        thirdparty: hasThirdparty,
        taxpayer: hasTaxpayer,
        documents: [],
        taxpayerVep: [],
    };
}

function buildArcaPaidInitialValues() {
    return {
        paidVepSubType: {
            value: "own",
            label: i18n.get(`${FORM_LIST_ID}.new.generatedVEP.options.owns`),
        },
        document: "",
        taxpayer: "",
        taxpayerVepNumber: "",
        paymentDate: [
            moment()
                .subtract(6, "months")
                .toDate(),
            moment().toDate(),
        ],
    };
}

function PaymentsServicesArcaPage({
    dispatch,
    location,
    embeddedInV3Shell,
    isDesktop,
    isFetching,
    fetchingPaymentsAFIP,
    servicePaymentsSelected,
    flatPayments,
    taxServicesCart,
    paymentsAFIPRows,
    multiplePaymentsInfo,
    activeEnvironment,
    accounts,
    isRetailEnvironment,
    moduleEntryPath,
    lastPathname,
    enabledContributors,
    defaultAccount,
    pageNumber,
    totalPages,
    paidVepsPageNumber,
    paidVepsTotalPages,
    paymentsOwn,
    paymentsThirdparty,
    paymentsTaxpayer,
    fetchingOwn,
    fetchingThirdparty,
    fetchingTaxpayer,
    fetchingPayDeleteAFIP,
    fetchingAccounts,
    fetchingToken,
    tokenAfip,
    hideBalance,
    activeFilters,
    arcSelectedVepType,
    arcActiveSection,
}) {
    const newLabel = configUtils.getBoolean("frontend.Show.ContextualMenu.Functionalities", false);
    const limitOfThirdPartyVeps = configUtils.getInteger("frontend.vepsPayments.thirdParty.amount", 5);
    const [lastApplyFilters, setLastApplyFilters] = useState(null);

    const supportsMultiplePayments = useMemo(() => {
        const showCorporate = configUtils.getBoolean(
            "frontend.show.MultiplePaymentTAX.functionalities.Corporate",
            false,
        );
        const showRetail = configUtils.getBoolean("frontend.show.MultiplePaymentTAX.functionalities.Retail", false);
        return isRetailEnvironment ? showRetail : showCorporate;
    }, [isRetailEnvironment]);

    const allowMultipleAFIPDetail = useMemo(() => {
        const showCorporate = configUtils.getBoolean(
            "frontend.show.MultiplePaymentVEP.functionalities.Corporate",
            false,
        );
        const showRetail = configUtils.getBoolean("frontend.show.MultiplePaymentVEP.functionalities.Retail", false);
        return isRetailEnvironment ? showRetail : showCorporate;
    }, [isRetailEnvironment]);

    const [showAfipServicesDrawer, setShowAfipServicesDrawer] = useState(false);
    const [multipleServicesPreparePending, setMultipleServicesPreparePending] = useState(false);

    const resolveExitHref = useCallback(() => {
        const isV3Ux = isUxDashboardImprovementsEnabled();
        const homePath = getPostLoginHomePath(isV3Ux);
        const current = location.pathname;

        const pick = [moduleEntryPath, lastPathname].find((p) => isEligibleExitHref(p) && p !== current);
        return (pick !== "/v2/payAFIPSummary" && pick) || homePath;
    }, [location.pathname, lastPathname, moduleEntryPath]);

    const hasInitializedArcaPage = useRef(false);

    useEffect(() => {
        if (hasInitializedArcaPage.current) {
            return;
        }
        hasInitializedArcaPage.current = true;
        saveLocation({ location, dispatch });
        dispatch(accountsActions.listAccounts());
        if (!multiplePaymentsInfo?.length) {
            dispatch(servicePaymentsActions.listAgendaPagos(isDesktop));
        }
        dispatch(paymentsAFIPActions.cleanBackFromOtherPage());
        if (moduleEntryPath == null && isEligibleExitHref(lastPathname) && lastPathname !== location.pathname) {
            dispatch(servicePaymentsActions.setModuleEntryPath(lastPathname));
        }
        dispatch(paymentsAFIPActions.listEnabledContributors());
    }, []);

    /**
     * Starts a new service payment. The payment categories are requested from this screen so the
     * user only navigates to `/newPayment` when the Link service answers successfully; on failure
     * the saga shows the error notification and the user stays here.
     */
    const handleNewPayment = useCallback(() => {
        if (accounts && accounts.length > 0) {
            dispatch(servicePaymentsActions.cleanSummary());
            dispatch(statusActions.saveLastHref(location));
            dispatch(servicePaymentsActions.listRubros(accounts[0], { successPath: "/newPayment" }));
            return;
        }
        dispatch(
            notificationActions.showNotification(i18n.get("newPayment.noAccounts.error"), "error", ["servicePayments"]),
        );
    }, [accounts, dispatch, location]);

    const mobileRightContent = useMemo(() => {
        if (isDesktop) {
            return undefined;
        }

        const items = [];

        if (activeEnvironment.permissions.newPayment) {
            items.push({
                label: `${FORM_ID}.newPayment`,
                onClick: handleNewPayment,
                icon: "PayService",
            });
        }

        items.push(
            {
                label: `${FORM_ID}.attachedServices`,
                onClick: () => {
                    dispatch(push("/adhesiones/list"));
                },
                icon: "AddServices",
            },
            {
                label: `${FORM_ID}.${newLabel ? "arca" : "afip"}Services`,
                onClick: () => {
                    if (!tokenAfip) {
                        dispatch(paymentsAFIPActions.getToken());
                    }
                    setShowAfipServicesDrawer(true);
                },
                icon: "Arca",
            },
            {
                label: "menu.payments.enabledContributors",
                onClick: () => {
                    dispatch(push("/payments/enabledContributors"));
                },
                icon: "UserCheck",
            },
        );

        return (
            <ContextMenu
                isDesktop={isDesktop}
                styleContext={{ justifyContent: "flex-end" }}
                color="white"
                items={items}
            />
        );
    }, [isDesktop, activeEnvironment.permissions.newPayment, newLabel, dispatch, handleNewPayment]);

    useEffect(() => {
        const isV3Ux = isUxDashboardImprovementsEnabled();
        const homePath = getPostLoginHomePath(isV3Ux);
        const pick = [moduleEntryPath, lastPathname].find((p) => isEligibleExitHref(p) && p !== location.pathname);
        const goBackPath = pick || homePath;
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["servicePayments"],
            titleKey: `menu.payments.payServices${newLabel ? ".arca" : ".Afip"}`,
            goBackPath,
            rightContentMobile: mobileRightContent,
        });
    }, [
        dispatch,
        embeddedInV3Shell,
        isDesktop,
        newLabel,
        lastPathname,
        moduleEntryPath,
        location.pathname,
        mobileRightContent,
    ]);

    const lastPaidVepFetchParamsRef = useRef(null);

    const servicePaymentsSelectedRef = useRef(servicePaymentsSelected);
    servicePaymentsSelectedRef.current = servicePaymentsSelected;
    const locationRef = useRef(location);
    locationRef.current = location;

    useEffect(
        () => () => {
            if (embeddedInV3Shell) {
                clearEmbeddedV3ShellLayoutState(dispatch);
            }
            dispatch(servicePaymentsActions.setService(servicePaymentsSelectedRef.current));
            dispatch(statusActions.saveLastHref(locationRef.current));
        },
        [dispatch, embeddedInV3Shell],
    );

    const handleBack = () => {
        dispatch(push(resolveExitHref()));
        dispatch(servicePaymentsActions.setModuleEntryPath(null));
    };

    const taxpayerVepReferenceHint = i18n.get(`${FORM_LIST_ID}.generatedVEP.taxpayer.hint`);
    const taxpayerOptions =
        enabledContributors?.map((c) => ({
            reference: c.referencia || taxpayerVepReferenceHint,
            value: c.cuit,
            label: c.cuit,
        })) ?? [];

    const navigateTaxServiceEntity = useCallback(
        (internalPath, event) => {
            if (event && typeof event.preventDefault === "function") {
                event.preventDefault();
            }
            if (internalPath) {
                dispatch(push(internalPath));
            }
        },
        [dispatch],
    );

    const applyTaxServicesCartDispatch = useCallback(
        (applyFn) => {
            dispatch(servicePaymentsActions.applyTaxServicesCart(applyFn));
        },
        [dispatch],
    );

    const listRows = useMemo(() => {
        const rows = flatPayments?.length ? flatPayments : [];
        const arsAmountLabel = i18n.get("currency.label.0");
        return rows.map((p) => flatPaymentToArcaTaxServiceRow(p, arsAmountLabel));
    }, [flatPayments]);

    const fetchTaxServicesAgendaPage = useCallback(
        (pn) => {
            const targetPage = pn ?? Math.min(pageNumber + 1, totalPages);
            dispatch(servicePaymentsActions.setPageNumber(targetPage));
            dispatch(servicePaymentsActions.listAgendaPagos(isDesktop));
        },
        [dispatch, isDesktop, pageNumber, totalPages],
    );

    const listPropsServicesPaginated = useMemo(
        () => ({
            array: listRows,
            paginationProps: {
                totalPages,
                pageNumber,
                action: fetchTaxServicesAgendaPage,
            },
            emptyStateText: i18n.get(
                "menu.payments.empty.message",
                "No tenés deudas informadas. Una vez que adhieras servicios, los verás aquí reflejados.",
            ),
        }),
        [listRows, fetchTaxServicesAgendaPage, pageNumber, totalPages],
    );

    const paidDetailLookup = useMemo(() => {
        const map = new Map();
        paymentsAFIPRows.forEach((raw) => {
            map.set(String(raw.numberVEP), raw);
        });
        return map;
    }, [paymentsAFIPRows]);

    const arsAccounts = useMemo(() => (accounts || []).filter((account) => isPesosAccount(account)), [accounts]);

    const initialDebitAccount = useMemo(() => {
        const pickFallback = () => {
            if (!arsAccounts.length) {
                return "";
            }
            const favorite = arsAccounts.find((acc) => acc.favorite);
            return String((favorite ?? arsAccounts[0]).idProduct);
        };

        if (typeof defaultAccount === "string" && defaultAccount !== "") {
            if (arsAccounts.some((a) => String(a.idProduct) === defaultAccount)) {
                return defaultAccount;
            }
            return pickFallback();
        }
        if (defaultAccount != null && typeof defaultAccount === "object") {
            const id = defaultAccount.idProduct ?? defaultAccount.id;
            if (id != null && arsAccounts.some((a) => String(a.idProduct) === String(id))) {
                return String(id);
            }
            return pickFallback();
        }
        return pickFallback();
    }, [arsAccounts, defaultAccount]);

    const accountsOptions = useMemo(
        () =>
            arsAccounts.map((a) => {
                let balanceAmount = a.partialAmounts?.available?.quantity ?? a.partialAmounts?.quantity ?? 0;
                if (typeof a.balance === "number") {
                    balanceAmount = a.balance;
                } else if (typeof a.partialAmount === "number") {
                    balanceAmount = a.partialAmount;
                }
                return {
                    id: String(a.idProduct),
                    accountName: a.productAlias || a.shortLabel || "",
                    description: a.productType ? i18n.get(`productType.${a.productType}`) : "",
                    currency: a.currency != null && a.currency !== "" ? i18n.get(`currency.label.${a.currency}`) : "",
                    amount: balanceAmount,
                    disabled: balanceAmount <= 0,
                };
            }),
        [arsAccounts],
    );

    const getArcaLabels = (id) => {
        const known = {
            taxandservices: i18n.get(`${FORM_ID}.services.label`),
            paymentsvep: i18n.get(`${FORM_ID}.${newLabel ? "ARCA" : "AFIP"}.label`),
            debitaccount: i18n.get(
                "services.multiplePayment.field.account",
                i18n.get("menu.payments.account.label", "Cuenta de débito"),
            ),
            totalTaxServicesAmount: i18n.get(
                "menu.payments.arca.summary.taxServicesTotalAmountSelected",
                "Monto total (seleccionados)",
            ),
            totalVepsPaymentsAmount: i18n.get(
                "menu.payments.arca.summary.vepsTotalAmountSelected",
                "Monto total (seleccionados)",
            ),
            totalVepsOperations: i18n.get("menu.payments.arca.summary.vepsOperationsSelected", "Seleccionados"),
            totalTaxServicesOperations: i18n.get(
                "menu.payments.arca.summary.taxServicesOperationsSelected",
                "Seleccionados",
            ),
            goToPaySelected: i18n.get("menu.payments.paySelected", "Pagar seleccionados"),
            selectAllTaxServices: i18n.get("payments.afip.list.selectMultiple", "Seleccionar todos"),
            selectAllPending: i18n.get("payments.afip.list.selectMultiple", "Seleccionar todos"),
            "see.filters": i18n.get("global.see.filter", "Ver filtros"),
            "hide.filters": i18n.get("global.hide.filter", "Ocultar filtros"),
            "active.filters": i18n.get("global.filter.active", "Filtros activos"),
            "clean.filters": i18n.get("global.clean.filters"),
            own: i18n.get(`${FORM_LIST_ID}.new.generatedVEP.options.owns`),
            thirdparty: i18n.get(`${FORM_LIST_ID}.new.generatedVEP.options.third`),
            documents: i18n.get(`${FORM_LIST_ID}.new.generatedVEP.third.label`),
            "documents.placeholder": i18n.get(`${FORM_LIST_ID}.new.generatedVEP.third.placeholder`),
            "documents.tooltip": i18n.get(`${FORM_LIST_ID}.documents.tooltip`, null, {
                limit: limitOfThirdPartyVeps,
            }),
            taxpayer: i18n.get(`${FORM_LIST_ID}.new.generatedVEP.options.taxpayer`),
            "taxpayer.label": i18n.get(`${FORM_LIST_ID}.generatedVEP.taxpayer.label`),
            "taxpayer.placeholder": i18n.get(`${FORM_LIST_ID}.generatedVEP.placeholder`),
            "taxpayer.noOptions": i18n.get(`${FORM_LIST_ID}.new.generatedVEP.noOptions`),
            taxpayerVep: i18n.get(`${FORM_LIST_ID}.numberVEP`),
            taxpayerVepNumber: i18n.get(`${FORM_LIST_ID}.numberVEP`),
            vepNumber: i18n.get(`${FORM_LIST_ID}.numberVEP.label`),
            "vepNumber.placeholder": "",
            taxpayerSelect: i18n.get(`${FORM_LIST_ID}.taxpayer`),
            paidVepSubType: i18n.get(`${FORM_LIST_ID}.generatedVEP.label`),
            document: i18n.get(`${FORM_LIST_ID}.thirdPartyCuit.label`),
            paymentDate: i18n.get(`${FORM_LIST_ID}.dates.label`),
            filter: i18n.get("global.filter"),
            pendingPay: i18n.get(`${FORM_LIST_ID}.paymentsType.stillToPay`, "Pendientes de pago"),
            paid: i18n.get(`${FORM_LIST_ID}.paymentsType.paymentsMade`, "Pagos realizados"),
            statusPending: i18n.get("services.payments.detail.status.PENDING"),
        };

        const keyParts = `${id}`;
        let resolved;
        if (Object.prototype.hasOwnProperty.call(known, keyParts)) {
            resolved = known[keyParts];
        } else {
            const lookupId = keyParts.includes(".") ? keyParts : `${FORM_LIST_ID}.${keyParts}`;
            resolved = i18n.get(lookupId, keyParts);
        }
        return resolved;
    };

    const getCardLabels = (cardId) => {
        const map = {
            expiration: ["pay.paymentAFIP.dueDate", String(cardId)],
            vepNumber: ["payments.arca.vepCard.vepNumberLabel", String(cardId)],
            concept: ["pay.paymentAFIP.concept", String(cardId)],
            subConcept: ["pay.paymentAFIP.subConcept", String(cardId)],
            taxpayer: ["pay.paymentAFIP.taxpayerCUIT", String(cardId)],
            fiscalPeriod: ["pay.paymentAFIP.fiscalPeriod", String(cardId)],
            pay: ["payments.afip.list.actions.pay", String(cardId)],
            delete: ["payments.afip.list.actions.delete", String(cardId)],
            advanceQuota: ["pay.paymentAFIP.advancePayment", String(cardId)],
            pending: ["pay.paymentAFIP.signPending", String(cardId)],
        };
        const entry = map[cardId];
        let raw;
        if (entry) {
            raw = i18n.get(entry[0], entry[1]);
        } else {
            raw = i18n.get(`${FORM_LIST_ID}.${cardId}`, String(cardId));
        }
        return raw;
    };

    const onSubmitArcaFilters = useCallback(
        (values) => {
            if (values?.activeSection === "paymentsvep") {
                const params = mapArcaPaidVepFiltersToFetchParams(values, { isDesktop });
                const defaultPn = "1";
                dispatch(paymentsAFIPActions.saveFormState(values));
                if (values?.selectedVepType === "pendingPay") {
                    const cleanFilters = values?.cleanFilters;
                    dispatch(paymentsAFIPActions.cleanPaymentsAfip());
                    const currFilters = {
                        own: cleanFilters ? false : values?.own,
                        thirdparty: cleanFilters ? false : values?.thirdparty,
                        documents: values?.documents || [],
                        taxpayer: cleanFilters ? false : values?.taxpayer,
                        taxpayerVep: values?.taxpayerVep || [],
                    };
                    setLastApplyFilters(currFilters);
                    dispatch(
                        paymentsAFIPActions.fetchPaymentsAFIPByFilter(
                            params.paymentType,
                            "1",
                            [],
                            defaultPn,
                            [],
                            values?.newSearch || false,
                            currFilters,
                        ),
                    );

                    dispatch(
                        paymentsAFIPActions.fetchPaymentsAFIPByFilter(
                            params.paymentType,
                            "2",
                            [],
                            defaultPn,
                            values?.thirdparty ? values.documents : [],
                            values?.newSearch || false,
                            currFilters,
                        ),
                    );

                    const contributors = values.taxpayerVep?.map((item) => ({
                        document: item.taxpayer?.value,
                        vepNumber: item.vepNumber,
                    }));
                    dispatch(
                        paymentsAFIPActions.fetchPaymentsAFIPByFilter(
                            params.paymentType,
                            "3",
                            values?.taxpayer ? contributors : [],
                            defaultPn,
                            [],
                            values?.newSearch || false,
                            currFilters,
                        ),
                    );
                } else {
                    lastPaidVepFetchParamsRef.current = params;
                    dispatch(
                        paymentsAFIPActions.fetchPaymentsAFIP(
                            params.paymentType,
                            params.generatedVEP,
                            params.numberVEP,
                            params.taxpayerCUIT,
                            params.dateFrom,
                            params.dateTo,
                            params.pageNumber,
                            params.thirdPartyCuit,
                            isDesktop,
                        ),
                    );
                }
            }
        },
        [dispatch, isDesktop],
    );

    const fetchPaidVepsPage = useCallback(
        (pn) => {
            const last = lastPaidVepFetchParamsRef.current;
            if (!last) {
                return;
            }
            const targetPage = pn ?? Math.min(paidVepsPageNumber + 1, paidVepsTotalPages);
            dispatch(
                paymentsAFIPActions.fetchPaymentsAFIP(
                    last.paymentType,
                    last.generatedVEP,
                    last.numberVEP,
                    last.taxpayerCUIT,
                    last.dateFrom,
                    last.dateTo,
                    String(targetPage),
                    last.thirdPartyCuit,
                    isDesktop,
                ),
            );
        },
        [dispatch, isDesktop, paidVepsPageNumber, paidVepsTotalPages],
    );

    const listPropsPaidVepsPaginated = useMemo(
        () => ({
            array: mapPaymentsAFIPToVepPaidCardData(paymentsAFIPRows),
            loading: fetchingPaymentsAFIP,
            emptyStateText: i18n.get(`${FORM_LIST_ID}.empty.message`, "No se encontraron pagos realizados"),
            paginationProps: {
                totalPages: paidVepsTotalPages,
                pageNumber: paidVepsPageNumber,
                action: fetchPaidVepsPage,
            },
        }),
        [paymentsAFIPRows, fetchingPaymentsAFIP, paidVepsTotalPages, paidVepsPageNumber, fetchPaidVepsPage],
    );

    const hasOwnVeps = paymentsOwn?.length > 0;
    const hasThirdpartyVeps = paymentsThirdparty?.length > 0;
    const hasTaxpayerVeps = paymentsTaxpayer?.length > 0;

    const arcaPendingInitialValues = useMemo(
        () => activeFilters ?? buildArcaPendingInitialValues(hasOwnVeps, hasThirdpartyVeps, hasTaxpayerVeps),
        [activeFilters, hasOwnVeps, hasThirdpartyVeps, hasTaxpayerVeps],
    );

    const arcaPendingBaselineValues = useMemo(() => buildArcaPendingInitialValues(false, false, false), []);

    const arcaPaidInitialValues = useMemo(() => buildArcaPaidInitialValues(), []);

    const arcaPaidValidationSchema = useMemo(
        () =>
            Yup.lazy((values) => {
                const paidSubRaw = values?.paidVepSubType?.value ?? values?.paidVepSubType;
                const subgroup =
                    paidSubRaw === "thirdparty" || paidSubRaw === "taxpayer" || paidSubRaw === "own"
                        ? paidSubRaw
                        : "own";

                const rangeDateMsg = i18n.get(`${FORM_LIST_ID}.validation.range.date`, "Este campo es requerido");

                const shape = {
                    paymentDate: Yup.mixed().test("paymentDateRange", rangeDateMsg, () => {
                        const parent = values || {};
                        const block = parent.paymentDate ?? parent.creationDates ?? parent.paymentDates;
                        if (block == null || typeof block !== "object") {
                            return false;
                        }
                        const from = block.dateFrom ?? block.from ?? block[0];
                        const to = block.dateTo ?? block.to ?? block[1];
                        return Boolean(from && to);
                    }),
                };

                if (subgroup === "thirdparty") {
                    shape.document = Yup.string().required(
                        i18n.get(`${FORM_LIST_ID}.thirdPartyCuit.error.required`, "El campo es requerido"),
                    );
                }

                if (subgroup === "taxpayer") {
                    shape.taxpayer = Yup.mixed()
                        .required(i18n.get(`${FORM_LIST_ID}.taxpayerCUIT.error.required`, "El campo es requerido"))
                        .test(
                            "taxpayerHasValue",
                            i18n.get(`${FORM_LIST_ID}.taxpayerCUIT.error.required`, "El campo es requerido"),
                            (v) => v != null && v !== "" && (typeof v !== "object" || Boolean(v.value)),
                        );
                    shape.taxpayerVepNumber = Yup.string().required(
                        i18n.get(`${FORM_LIST_ID}.numberVEP.error.required`, "El campo es requerido"),
                    );
                }

                return Yup.object().shape(shape);
            }),
        [],
    );

    const handleGoToPayServices = useCallback(
        (_accountUiKit) => {
            const cartRows = Object.values(taxServicesCart || {});

            if (!supportsMultiplePayments) {
                if (cartRows.length !== 1) {
                    dispatch(
                        notificationActions.showNotification(
                            i18n.get(
                                "payments.arca.validation.selectOneService",
                                "Seleccioná un servicio para continuar.",
                            ),
                            "error",
                            ["servicePayments"],
                        ),
                    );
                    return;
                }
                const svc = cartRows[0];
                const fpRaw = svc?.sourceFlatPayment;
                const debit = accounts.find((a) => String(a.idProduct) === _accountUiKit.id);
                if (!fpRaw || !debit) {
                    return;
                }
                const fp = mergeFlatPaymentWithArcaServiceRow({ ...fpRaw }, svc);
                dispatch(servicePaymentsActions.loadSummaryInfo(buildSummaryInfo(fp, debit)));
                dispatch(statusActions.saveLastHref(location));
                dispatch(servicePaymentsActions.applyTaxServicesCart(() => ({})));
                dispatch(push("/agenda/payment"));
                return;
            }

            const debit = accounts.find((a) => String(a.idProduct) === _accountUiKit.id);
            if (!debit) {
                return;
            }
            const synced = cartRows
                .map((svc) => {
                    const payment = svc?.sourceFlatPayment;
                    if (!payment) {
                        return null;
                    }
                    return {
                        ...mergeFlatPaymentWithArcaServiceRow({ ...payment }, svc),
                        isChecked: true,
                    };
                })
                .filter(Boolean);

            if (synced.length === 0) {
                dispatch(
                    notificationActions.showNotification(
                        i18n.get("payments.arca.validation.selectOneService", "Seleccioná un servicio para continuar."),
                        "error",
                        ["servicePayments"],
                    ),
                );
                return;
            }

            const debitIdProduct = debit.idProduct;
            dispatch(servicePaymentsActions.updatePaymentsInfo(synced));
            dispatch(servicePaymentsActions.setMultiplePaymentsAccount(debit));

            setMultipleServicesPreparePending(true);
            const stubFormik = {
                setTouched: () => {},
                setErrors: () => {},
                props: {
                    dispatch,
                    paymentsInfo: synced,
                    account: debitIdProduct,
                },
                setSubmitting: (isSubmitting) => {
                    if (isSubmitting === false) {
                        setMultipleServicesPreparePending(false);
                    }
                },
            };
            const preparePayments = buildPreparePaymentsRequest(synced);
            dispatch(servicePaymentsActions.cleanHandleBackFromTicket());
            dispatch(servicePaymentsActions.prepareMultiplePayments(preparePayments, debitIdProduct, stubFormik));
        },
        [accounts, taxServicesCart, supportsMultiplePayments, dispatch, location],
    );

    const handlePayVeps = useCallback(
        (_accountUiKit, selectedAfip) => {
            const debit = accounts.find((a) => String(a.idProduct) === _accountUiKit.id);
            if (!debit) {
                return;
            }
            const result = Object.entries(selectedAfip).reduce((acc, [key, value]) => {
                const type = key.split(":")[0];

                if (!acc[type]) {
                    acc[type] = [];
                }
                acc[type].push(value);
                return acc;
            }, {});

            dispatch(paymentsAFIPActions.payMultipleVep(result, debit, lastApplyFilters));
        },
        [accounts, lastApplyFilters],
    );

    const renderPaymentAFIP = (selectedAfip, vepType) => {
        let vepPayment = null;
        switch (vepType) {
            case "own":
                vepPayment = paymentsOwn.find((p) => p.numberVEP === selectedAfip?.vepNumber);
                break;
            case "thirdparty":
                vepPayment = paymentsThirdparty.find((p) => p.numberVEP === selectedAfip?.vepNumber);
                break;
            case "taxpayer":
                vepPayment = paymentsTaxpayer.find((p) => p.numberVEP === selectedAfip?.vepNumber);
                break;
            default:
                return null;
        }
        return vepPayment;
    };

    const handleDeletePaymentAFIP = (selectedAfip, vepType) => {
        const vepPayment = renderPaymentAFIP(selectedAfip, vepType);
        dispatch(paymentsAFIPActions.deletePaymentAFIPPreFromMain(vepPayment, false, lastApplyFilters));
    };

    const readPaymentAFIP = (selectedAfip, vepType) =>
        dispatch(
            paymentsAFIPActions.readMultiplePaymentAFIP(renderPaymentAFIP(selectedAfip, vepType), lastApplyFilters),
        );

    const loading = !!(isFetching || fetchingPaymentsAFIP || fetchingAccounts);

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="servicePayments" />}
            {!embeddedInV3Shell && !isDesktop && (
                <div className="admin-detail-head px-0 mb-0">
                    {isDesktop ? <Head onBack={handleBack} /> : undefined}
                    <Head
                        title={`menu.payments.payServices${newLabel ? ".arca" : "Afip"}`}
                        rightContent={() => mobileRightContent}
                        isFetchingExport={false}
                        onBack={!isDesktop ? handleBack : undefined}
                        headerClassName={
                            !isDesktop ? "blue-main-header-mobile servive-payment-title-mobile" : undefined
                        }
                    />
                </div>
            )}
            {isDesktop ? (
                <Drawer
                    isVisible={showAfipServicesDrawer}
                    onClose={() => setShowAfipServicesDrawer(false)}
                    placement="right"
                    searcher={false}
                    w={500}>
                    {showAfipServicesDrawer && !fetchingToken ? (
                        <AFIPServiceConditions
                            closeDrawer={() => setShowAfipServicesDrawer(false)}
                            isDesktop={isDesktop}
                        />
                    ) : (
                        <Box flex justify="center" align="center" column py={72}>
                            <Spinner size="large" />
                        </Box>
                    )}
                </Drawer>
            ) : (
                <BottomSheet
                    isVisible={showAfipServicesDrawer}
                    onClose={() => setShowAfipServicesDrawer(false)}
                    iconTitle="Arca"
                    iconTitleColor="primary-color"
                    title={i18n.get(`payments.service${newLabel ? "ARCA" : "AFIP"}.title`)}>
                    {showAfipServicesDrawer && !fetchingToken ? (
                        <AFIPServiceConditions
                            closeDrawer={() => setShowAfipServicesDrawer(false)}
                            isDesktop={isDesktop}
                        />
                    ) : (
                        <Box flex justify="center" align="center" column py={72}>
                            <Spinner size="large" />
                        </Box>
                    )}
                </BottomSheet>
            )}
            <Box px={isUxDashboardImprovementsEnabled() || !isDesktop ? 0 : 20}>
                <PaymentServicesArca
                    loading={loading || fetchingPayDeleteAFIP || fetchingOwn || fetchingThirdparty || fetchingTaxpayer}
                    emptyStateText={i18n.get(`${FORM_LIST_ID}.empty.new.message`, i18n.get(`${FORM_ID}.empty.message`))}
                    disabledSearch={false}
                    emptySearchVepsText={i18n.get(
                        `${FORM_LIST_ID}.empty.search.message`,
                        "Realizá una búsqueda para ver los resultados.",
                    )}
                    validationSchema={Yup.object()}
                    paidValidationSchema={arcaPaidValidationSchema}
                    enableHideAccountBalance={
                        configUtils.getBoolean("frontend.hide.accounts.balance.funcionalities") &&
                        Boolean(hideBalance) &&
                        !isDesktop
                    }
                    filterText={configUtils.get(
                        "frontend.vepsPayments.results.tooltip",
                        i18n.get(
                            "payments.arca.filter.lastSearchDisclaimer",
                            "Estás viendo los resultados de tu última búsqueda. Si querés ver algo distinto, ajustá los filtros.",
                        ),
                    )}
                    getLabels={getArcaLabels}
                    getCardLabel={getCardLabels}
                    getServiceCardLabel={getPaymentsArcaServiceCardLabel}
                    onTaxServiceEntityNavigate={navigateTaxServiceEntity}
                    taxServicesCart={taxServicesCart}
                    onApplyTaxServicesCart={applyTaxServicesCartDispatch}
                    vepGroupsList={{
                        own: mapPaymentsAFIPToVepPaidCardData(paymentsOwn),
                        thirdparty: mapPaymentsAFIPToVepPaidCardData(paymentsThirdparty),
                        taxpayer: mapPaymentsAFIPToVepPaidCardData(paymentsTaxpayer),
                    }}
                    onSubmit={(values) => onSubmitArcaFilters(values)}
                    pendingInitialValues={arcaPendingInitialValues}
                    paidInitialValues={arcaPaidInitialValues}
                    pendingBaselineValues={arcaPendingBaselineValues}
                    initialVepType="pendingPay"
                    arcSelectedVepType={arcSelectedVepType}
                    onArcaSelectedVepTypeChange={(vepType) =>
                        dispatch(paymentsAFIPActions.setArcSelectedVepType(vepType))
                    }
                    arcActiveSection={arcActiveSection}
                    onArcaActiveSectionChange={(section) => dispatch(paymentsAFIPActions.setArcActiveSection(section))}
                    initialDebitAccount={initialDebitAccount}
                    accountsOptions={accountsOptions}
                    taxpayerOptions={taxpayerOptions}
                    limitOfThirdPartyVeps={limitOfThirdPartyVeps}
                    limitOfTaxpayerVeps={configUtils.getInteger("frontend.vepsPayments.toContributor.amount", 5)}
                    handleDeletePayment={handleDeletePaymentAFIP}
                    handleOnClickVep={readPaymentAFIP}
                    pageHeaderProps={{
                        goBack: handleBack,
                        title: i18n.get(`menu.payments.payServices${newLabel ? ".arca" : ".Afip"}`),
                        rightOptions: [
                            ...(activeEnvironment.permissions.newPayment
                                ? [
                                      {
                                          icon: "Add",
                                          iconVariant: "outline",
                                          label: i18n.get(`${FORM_ID}.newPayment`),
                                          buttonVariant: "solid",
                                          buttonColor: "primary",
                                          callback: handleNewPayment,
                                          w: "200px",
                                      },
                                  ]
                                : []),
                            {
                                icon: "AddServices",
                                iconVariant: "outline",
                                label: i18n.get(`${FORM_ID}.attachedServices`),
                                buttonVariant: "outline",
                                buttonColor: "primary",
                                callback: () => dispatch(push("/adhesiones/list")),
                                w: "200px",
                            },
                            {
                                icon: "Arca",
                                iconVariant: "outline",
                                label: i18n.get(`${FORM_ID}.${newLabel ? "arca" : "afip"}Services`),
                                buttonVariant: "outline",
                                buttonColor: "primary",
                                callback: () => {
                                    if (!tokenAfip) {
                                        dispatch(paymentsAFIPActions.getToken());
                                    }
                                    setShowAfipServicesDrawer(true);
                                },
                            },
                            {
                                icon: "UserCheck",
                                iconVariant: "outline",
                                label: i18n.get("menu.payments.enabledContributors"),
                                buttonVariant: "outline",
                                buttonColor: "primary",
                                callback: () => dispatch(push("/payments/enabledContributors")),
                            },
                        ],
                        renderOptionsAs: "default",
                        renderOptionsInMobile: true,
                    }}
                    listPropsPaidVeps={listPropsPaidVepsPaginated}
                    listPropsServices={listPropsServicesPaginated}
                    multipleServicesPreparePending={multipleServicesPreparePending}
                    handleGoToPayServices={handleGoToPayServices}
                    onPaidVepDetail={(item) => {
                        const raw = paidDetailLookup.get(String(item.vepNumber));
                        if (raw) {
                            if (allowMultipleAFIPDetail) {
                                dispatch(paymentsAFIPActions.readMultiplePaymentAFIP(raw));
                            } else {
                                dispatch(paymentsAFIPActions.readPaymentAFIP(raw));
                            }
                        }
                    }}
                    handleGoToPayVeps={handlePayVeps}
                    initialSection={servicePaymentsSelected ? "taxandservices" : "paymentsvep"}
                />
            </Box>
        </>
    );
}

PaymentsServicesArcaPage.propTypes = {
    dispatch: PropTypes.func.isRequired,
    location: PropTypes.shape({
        pathname: PropTypes.string,
    }).isRequired,
    embeddedInV3Shell: PropTypes.bool,
    isDesktop: PropTypes.bool.isRequired,
    isFetching: PropTypes.bool.isRequired,
    fetchingPaymentsAFIP: PropTypes.bool.isRequired,
    servicePaymentsSelected: PropTypes.bool.isRequired,
    flatPayments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    taxServicesCart: PropTypes.objectOf(PropTypes.shape({})).isRequired,
    paymentsAFIPRows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    multiplePaymentsInfo: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    activeEnvironment: PropTypes.shape({
        permissions: PropTypes.shape({
            newPayment: PropTypes.bool,
        }),
    }).isRequired,
    accounts: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    isRetailEnvironment: PropTypes.bool,
    moduleEntryPath: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
    lastPathname: PropTypes.string.isRequired,
    enabledContributors: PropTypes.arrayOf(PropTypes.shape({})),
    defaultAccount: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    pageNumber: PropTypes.number.isRequired,
    totalPages: PropTypes.number.isRequired,
    paidVepsPageNumber: PropTypes.number.isRequired,
    paidVepsTotalPages: PropTypes.number.isRequired,
    paymentsOwn: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    paymentsThirdparty: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    paymentsTaxpayer: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    fetchingOwn: PropTypes.bool.isRequired,
    fetchingThirdparty: PropTypes.bool.isRequired,
    fetchingTaxpayer: PropTypes.bool.isRequired,
    fetchingPayDeleteAFIP: PropTypes.bool.isRequired,
    fetchingAccounts: PropTypes.bool.isRequired,
    fetchingToken: PropTypes.bool.isRequired,
    tokenAfip: PropTypes.string,
    hideBalance: PropTypes.bool,
    activeFilters: PropTypes.shape({}),
    arcSelectedVepType: PropTypes.oneOfType([PropTypes.oneOf(["paid", "pendingPay"]), PropTypes.oneOf([null])]),
    arcActiveSection: PropTypes.oneOfType([
        PropTypes.oneOf(["taxandservices", "paymentsvep"]),
        PropTypes.oneOf([null]),
    ]),
};

PaymentsServicesArcaPage.defaultProps = {
    embeddedInV3Shell: false,
    isRetailEnvironment: false,
    moduleEntryPath: null,
    enabledContributors: [],
    defaultAccount: null,
    tokenAfip: null,
    hideBalance: false,
    activeFilters: null,
    arcSelectedVepType: null,
    arcActiveSection: null,
};

const mapStateToProps = (state) => ({
    isFetching: servicePaymentsSelectors.getFetching(state),
    fetchingPaymentsAFIP: paymentsAFIPSelectors.getFetchingPaymentsAFIP(state),
    fetchingPayDeleteAFIP: paymentsAFIPSelectors.getFetchingPayOrDelete(state),
    servicePaymentsSelected: servicePaymentsSelectors.getServiceSelected(state),
    flatPayments: servicePaymentsSelectors.getFlatPayments(state),
    taxServicesCart: servicePaymentsSelectors.getTaxServicesCart(state),
    multiplePaymentsInfo: servicePaymentsSelectors.getMultiplePaymentsInfo(state),
    paymentsAFIPRows: paymentsAFIPSelectors.getMultiplePaymentsAFIP(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    accounts: accountsSelectors.getAccounts(state),
    fetchingAccounts: accountsSelectors.getFetching(state),
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
    lastPathname: statusSelectors.getPathname(state),
    moduleEntryPath: servicePaymentsSelectors.getModuleEntryPath(state),
    enabledContributors: paymentsAFIPSelectors.getEnabledContributors(state),
    defaultAccount: servicePaymentsSelectors.getDefaultAccount(state),
    pageNumber: servicePaymentsSelectors.getPageNumber(state),
    totalPages: servicePaymentsSelectors.getTotalPages(state),
    paidVepsPageNumber: paymentsAFIPSelectors.getPageNumber(state),
    paidVepsTotalPages: paymentsAFIPSelectors.getTotalPages(state),
    fetchingOwn: paymentsAFIPSelectors.getFetchingPaymentsOwn(state),
    fetchingThirdparty: paymentsAFIPSelectors.getFetchingPaymentsThirdparty(state),
    fetchingTaxpayer: paymentsAFIPSelectors.getFetchingPaymentsTaxpayer(state),
    paymentsOwn: paymentsAFIPSelectors.getPaymentsOwn(state),
    paymentsThirdparty: paymentsAFIPSelectors.getPaymentsThirdparty(state),
    paymentsTaxpayer: paymentsAFIPSelectors.getPaymentsTaxpayer(state),
    fetchingToken: paymentsAFIPSelectors.getFetching(state),
    tokenAfip: paymentsAFIPSelectors.getTokenAFIP(state),
    hideBalance: settingsSelectors.getUserData(state)?.hideBalance,
    activeFilters: paymentsAFIPSelectors.getActiveFilters(state),
    arcSelectedVepType: paymentsAFIPSelectors.getArcSelectedVepType(state),
    arcActiveSection: paymentsAFIPSelectors.getArcActiveSection(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(PaymentsServicesArcaPage);
